/**
 * Accessibility patches for third-party widgets whose source we don't control.
 *
 * Each patch targets a specific axe-core / WCAG failure reported on the live
 * site. We patch in theme code because the offending markup is injected by
 * vendor apps (Hotjar via GTM) that we can't modify directly.
 *
 * If a vendor ever fixes their markup upstream, the corresponding patch here
 * becomes a no-op and can be removed.
 */
(function a11yThirdPartyPatches() {
  const patchedHotjarDialogs = new WeakSet();

  init();

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }

    // Hotjar injects its survey markup asynchronously after GTM fires,
    // so we re-scan on every DOM mutation.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          patchHotjar(node);
        });
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function run() {
    patchHotjar(document);
  }

  /**
   * Patch: Hotjar survey dialog
   *
   * Axe rule: "An element with aria-hidden=true contains focusable content"
   * (aria-hidden-focus).
   *
   * Hotjar (loaded via GTM) renders its survey as a regular DOM dialog
   * (not an iframe) and toggles `aria-hidden` to show/hide it, but leaves the
   * buttons inside focusable when hidden. We mirror `inert` to `aria-hidden`
   * so the hidden survey is removed from the tab order too. Observing
   * attribute changes keeps them in sync if Hotjar re-opens the survey.
   */
  function patchHotjar(root) {
    forEachMatch(root, '[aria-labelledby^="hj-survey-lbl"]', (dialog) => {
      if (patchedHotjarDialogs.has(dialog)) return;
      patchedHotjarDialogs.add(dialog);
      syncHotjarInert(dialog);
      new MutationObserver(() => syncHotjarInert(dialog)).observe(dialog, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    });
  }

  function syncHotjarInert(dialog) {
    if (dialog.getAttribute('aria-hidden') === 'true') {
      dialog.setAttribute('inert', '');
    } else {
      dialog.removeAttribute('inert');
    }
  }

  // Runs `fn` against any element matching `selector` within `root`, including
  // `root` itself. Needed because `querySelectorAll` only searches descendants,
  // but the MutationObserver passes the matching element itself as the root.
  function forEachMatch(root, selector, fn) {
    if (root.matches && root.matches(selector)) fn(root);
    if (root.querySelectorAll) root.querySelectorAll(selector).forEach(fn);
  }
})();
